import React from 'react';
import styled from 'styled-components';
import moment from 'moment';

const breakpoints = {
  mobile: '320px',
  tablet: '768px',
  laptop: '1024px',
};

const Container = styled.div`
  margin-top: 32px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  width: 100%;
  padding-left: 32px;
  border-top: 3px solid #E6E6E6;
  padding-top: 24px;
  padding-bottom: 24px;

  @media (max-width: ${breakpoints.mobile}) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
  max-width: 1200px;
  padding: 0;
  justify-content: flex-start;
  margin: 0;
  
  @media (max-width: ${breakpoints.tablet}) {
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
  }
`;

const TimeStamp = styled.div`
  color: #666666;
  font-size: 16px;
  margin-bottom: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  padding: 0 50px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
    font-size: 14px;
  }
`;

const ScoreInfo = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  padding: 0 50px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
    flex-direction: column;
    gap: 8px;
  }
`;

const InfoItem = styled.div`
  color: #1a1a1a;
  font-size: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 500;
`;

const getBackgroundColor = (score) => {
  if (score >= 60) return '#BFF4E0';
  if (score >= 30) return '#FFF2D4';
  return '#FBDFE1';
};

const getBorderColor = (score) => {
  if (score >= 60) return '#5DB594';
  if (score >= 30) return '#FFC849';
  return '#F54B56';
};

const Card = styled.div`
  flex: 0 0 176px;
  height: 142px;
  background: ${props => getBackgroundColor(props.score)};
  border: 1px solid ${props => getBorderColor(props.score)};
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  text-align: center;
  gap: 12px;
  scroll-snap-align: start;

  @media (max-width: ${breakpoints.mobile}) {
    flex: 0 0 100%;
    height: auto;
    min-height: 120px;
  }
`;

const CardTitle = styled.div`
  font-size: 16px;
  color: #1A1A1A;
  font-weight: 400;
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-align: center;
  line-height: 1.4;
`;

const Title = styled.h3`
  font-size: 16px;
  color: #1a1a1a;
  margin-bottom: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  padding: 0 50px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 0;
    text-align: left;
  }
`;

const Score = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #1A1A1A;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const CommunicationAssessment = ({ data }) => {
  const defaultData = {
    timestamp: new Date(),
    englishLang: 0,
    readingAbility: 0,
    writingScore: 0,
    comprehension: 0,
    overallScore: 0,
    cefrLevel: 'N/A'
  };

  const assessmentData = data || defaultData;

  return (
    <Container>
      <Title>Communication Assessment</Title>
      <TimeStamp>
        Time Stamp : {moment(assessmentData.timestamp).format('DD/MM/YYYY | hh:mm A')}
      </TimeStamp>
      
      {/* ADD: Overall Score and CEFR Level */}
      <ScoreInfo>
        <InfoItem>Overall Score: {assessmentData.overallScore}%</InfoItem>
        <InfoItem>CEFR Level: {assessmentData.cefrLevel}</InfoItem>
      </ScoreInfo>

      <CardsContainer>
        <Card score={assessmentData.englishLang}>
          <CardTitle>Speaking Ability</CardTitle>
          <Score>{assessmentData.englishLang}%</Score>
        </Card>
        <Card score={assessmentData.readingAbility}>
          <CardTitle>Reading Ability</CardTitle>
          <Score>{assessmentData.readingAbility}%</Score>
        </Card>
        
        {/* ADD: Writing Section */}
        <Card score={assessmentData.writingScore}>
          <CardTitle>Writing Ability</CardTitle>
          <Score>{assessmentData.writingScore}%</Score>
        </Card>
        
        <Card score={assessmentData.comprehension}>
          <CardTitle>Listening Ability</CardTitle>
          <Score>{assessmentData.comprehension}%</Score>
        </Card>
      </CardsContainer>
    </Container>
  );
};

export default CommunicationAssessment;